import { TanStackDevtools } from '@tanstack/react-devtools';
import type { QueryClient } from '@tanstack/react-query';
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouterState,
} from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { Component, useEffect } from 'react';
import { Toaster } from 'sonner';

import ErrorPage from '../components/ErrorPage';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { JsonLdWrapper } from '../components/SEO';
import TanStackQueryDevtools from '../integrations/tanstack-query/devtools';
import { reportWebVitals } from '../lib/web-vitals';
import appCss from '../styles.css?url';

class ErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <ErrorPage />;
    }
    return this.props.children;
  }
}

interface MyRouterContext {
  queryClient: QueryClient;
}

const CLIENT = import.meta.env.VITE_APP_URL || 'http://localhost:3000';

export const Route = createRootRouteWithContext<MyRouterContext>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover',
      },
      {
        title: 'Azelysse Piercing & Tattoo',
      },
      {
        name: 'description',
        content:
          "Studio de piercing et tatouage à Égly (91520) - Essonne (91). Azelysse propose du piercing personnalisé avec bijoux en titane ASTM F-136, et des tatouages sur-mesure. À 5min d'Arpajon et 10min de Brétigny-sur-Orge. Gare RER C à Égly.",
      },
      {
        name: 'theme-color',
        content: '#fffbeb',
      },
      {
        name: 'referrer',
        content: 'strict-origin-when-cross-origin',
      },
      {
        property: 'og:title',
        content: 'Azelysse Piercing & Tattoo',
      },
      {
        property: 'og:description',
        content:
          "Studio de piercing et tatouage à Égly (91520) - Essonne (91). Azelysse propose du piercing personnalisé avec bijoux en titane ASTM F-136, et des tatouages sur-mesure. À 5min d'Arpajon et 10min de Brétigny-sur-Orge. Gare RER C à Égly.",
      },
      {
        property: 'og:image',
        content: `${CLIENT}/og-image-v2.png`,
      },
      {
        property: 'og:image:width',
        content: '1200',
      },
      {
        property: 'og:image:height',
        content: '630',
      },
      {
        property: 'og:image:alt',
        content: 'Azelysse Piercing & Tattoo',
      },
      {
        property: 'og:site_name',
        content: 'Azelysse Piercing & Tattoo',
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:locale',
        content: 'fr_FR',
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:title',
        content: 'Azelysse Piercing & Tattoo',
      },
      {
        name: 'twitter:description',
        content:
          'Studio de piercing et tatouage à Égly (91520), Essonne. Piercing personnalisé avec bijoux en titane ASTM F-136 et tatouages sur-mesure.',
      },
      {
        name: 'twitter:image',
        content: `${CLIENT}/og-image-v2.png`,
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
      {
        rel: 'icon',
        href: '/favicon.png',
      },
      ...(import.meta.env.VITE_API_URL
        ? [
            {
              rel: 'preconnect',
              href: import.meta.env.VITE_API_URL as string,
            },
          ]
        : []),
    ],
    scripts: [
      {
        src: 'https://js-de.sentry-cdn.com/2f3d54e3b4a05d7f5fe5d4e1b5ad1c14.min.js',
        crossOrigin: 'anonymous',
      },
      ...(import.meta.env.PROD
        ? [
            {
              src: 'https://plausible.cl3tusdev.com/js/script.js',
              defer: true,
              'data-domain': 'azelysse.fr',
            },
          ]
        : []),
    ],
  }),

  shellComponent: RootDocument,
  component: RootComponent,
});

function RootComponent() {
  const routerState = useRouterState();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [routerState.location.pathname]);

  useEffect(() => {
    reportWebVitals();
  }, []);

  return <Outlet />;
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="fr" className="scroll-smooth" suppressHydrationWarning>
      <head>
        <script
          dangerouslySetInnerHTML={{
            __html: `window.sentryOnLoad = function() {
              Sentry.init({
                dsn: "${import.meta.env.VITE_SENTRY_DSN}",
                environment: "${import.meta.env.MODE}",
                release: "${import.meta.env.VITE_APP_VERSION}",
                tracesSampleRate: ${import.meta.env.PROD ? 0.2 : 1.0},
                replaysSessionSampleRate: 0.1,
                replaysOnErrorSampleRate: 1.0,
              });
            };`,
          }}
        />
        <HeadContent />
        {import.meta.env.DEV && (
          <script
            dangerouslySetInnerHTML={{
              __html: `window.plausible = function() {
                var args = Array.prototype.slice.call(arguments);
                console.log('%c[Plausible]', 'color: #6366f1; font-weight: bold', args[0], args[1] || '');
              };`,
            }}
          />
        )}
      </head>
      <body className="flex min-h-screen flex-col">
        <svg
          className="absolute inset-0 -z-10 h-full w-full mask-[radial-gradient(100%_100%_at_top_right,white,transparent)] stroke-black/10"
          aria-hidden="true"
        >
          <defs>
            <pattern
              id="983e3e4c-de6d-4c3f-8d64-b9761d1534cc"
              width={200}
              height={200}
              x="50%"
              y={-1}
              patternUnits="userSpaceOnUse"
            >
              <path d="M.5 200V.5H200" fill="none" />
            </pattern>
          </defs>
          <svg x="50%" y={-1} className="overflow-visible fill-amber-100/20">
            <path
              d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
              strokeWidth={0}
            />
          </svg>
          <rect
            width="100%"
            height="100%"
            strokeWidth={0}
            fill="url(#983e3e4c-de6d-4c3f-8d64-b9761d1534cc)"
          />
        </svg>
        <Toaster richColors position="top-right" closeButton duration={8000} />
        <JsonLdWrapper />
        <Header />
        <main>
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
        <Footer />
        <TanStackDevtools
          config={{
            position: 'bottom-right',
          }}
          plugins={[
            {
              name: 'Tanstack Router',
              render: <TanStackRouterDevtoolsPanel />,
            },
            TanStackQueryDevtools,
          ]}
        />
        <Scripts />
      </body>
    </html>
  );
}
